import { useState, MouseEvent } from 'react';
import {
  ListItem,
  ListItemIcon,
  Typography,
  makeStyles,
  createStyles,
  IconButton,
  Menu,
  MenuItem,
  Box,
} from '@material-ui/core';
import DashboardIcon from '@material-ui/icons/DescriptionOutlined';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Draggable } from 'react-beautiful-dnd';

import SharedIndicator from 'src/components/sidebar/SharedIndicator';
import { Dashboard } from 'src/types';

export const useStyles = makeStyles(() =>
  createStyles({
    container: {
      padding: 0,
      alignItems: 'flex-start',
    },
    content: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flex: 1,
      minWidth: 0,
    },
  })
);

interface DashboardItemProps {
  dashboard: Dashboard;
  index: number;
  noDraggable?: boolean;
  showActions?: boolean;
  onSelectDashboard: (dashboard: Dashboard) => void;
  onShareDashboard?: (dashboard: Dashboard) => void;
  onLeaveDashboard?: (dashboard: Dashboard) => void;
  /** Recipient-side incoming share label. */
  hideSharedLabel?: boolean;
  /** Owner-side: this resource is shared with other orgs. */
  isSharedOut?: boolean;
  /** Owner-side org count from GET /share/owned. */
  sharedOutOrganizationCount?: number;
}

export const DashboardItem = ({
  dashboard,
  index,
  noDraggable = false,
  showActions = false,
  onSelectDashboard,
  onShareDashboard,
  onLeaveDashboard,
  hideSharedLabel = false,
  isSharedOut = false,
  sharedOutOrganizationCount,
}: DashboardItemProps) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const isShared = Boolean(dashboard.isShared);
  const showIncomingSharedLabel = isShared && !hideSharedLabel;
  const showOutgoingSharedLabel = !isShared && isSharedOut;
  const showSharedLabel = showIncomingSharedLabel || showOutgoingSharedLabel;
  const showShare = Boolean(showActions && onShareDashboard && !isShared);
  const showLeave = Boolean(showActions && onLeaveDashboard && isShared);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleShare = (event: MouseEvent) => {
    event.stopPropagation();
    handleCloseMenu();
    onShareDashboard?.(dashboard);
  };

  const handleLeave = (event: MouseEvent) => {
    event.stopPropagation();
    handleCloseMenu();
    onLeaveDashboard?.(dashboard);
  };

  const actionsMenu =
    showShare || showLeave ? (
      <div onClick={(e) => e.stopPropagation()}>
        <IconButton
          onClick={handleOpenMenu}
          style={{ padding: 2, marginLeft: 4 }}
          size='small'
        >
          <MoreVertIcon htmlColor='#757575' style={{ fontSize: 16 }} />
        </IconButton>
        <Menu
          id={`dashboard-item-menu-${dashboard.id}`}
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCloseMenu}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {showShare && <MenuItem onClick={handleShare}>Share</MenuItem>}
          {showLeave && (
            <MenuItem onClick={handleLeave} style={{ color: '#ff0000' }}>
              Leave
            </MenuItem>
          )}
        </Menu>
      </div>
    ) : null;

  const row = (
    <>
      <ListItemIcon style={{ minWidth: 30 }}>
        <DashboardIcon htmlColor='#757575' style={{ fontSize: 18 }} />
      </ListItemIcon>
      <Box className={classes.content}>
        <Typography
          style={{
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '18px',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {dashboard.name}
        </Typography>
        {showSharedLabel && (
          <SharedIndicator
            title={showIncomingSharedLabel ? 'Shared with you' : undefined}
            organizationCount={
              showOutgoingSharedLabel ? sharedOutOrganizationCount : undefined
            }
            onClick={
              showOutgoingSharedLabel && onShareDashboard
                ? () => onShareDashboard(dashboard)
                : undefined
            }
          />
        )}
        {actionsMenu}
      </Box>
    </>
  );

  if (noDraggable || isShared) {
    return (
      <ListItem
        button
        className={classes.container}
        onClick={() => {
          onSelectDashboard(dashboard);
        }}
      >
        {row}
      </ListItem>
    );
  }

  return (
    <Draggable draggableId={dashboard.id.toString()} index={index}>
      {(provided) => (
        <ListItem
          button
          className={classes.container}
          onClick={() => {
            onSelectDashboard(dashboard);
          }}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          {row}
        </ListItem>
      )}
    </Draggable>
  );
};
